
import { Article, Category, Translation } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { pageClickParams } from '@wix/bi-logger-customer-care-data/v2/types';
import { Box } from '@wix/design-system';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { animateScroll } from 'react-scroll';

import {
  BI,
  KNOWN_ISSUE_CATEGORY_ID,
  WIXEL_CATEGORY_ID,
} from '../../constants';
import { useBI } from '../../hooks/useBI';
import { PageType } from '../../types';
import {
  findCategoryInTree,
  getCategoriesBreadcrumb,
} from '../../utils/categories';
import { Breadcrumbs } from '../Breadcrumbs';
import { pushUrl } from '../InnerLink';
import { NavCategoryTree, NavigationTopics } from '../NavCategoryTree';
import { ArticleList } from './ArticleList';
import { CategoryPageHeader } from './CategoryPageHeader';
import { StudioCategoryAddon } from './StudioCategoryAddon';
import { SubcategoryCard } from './SubcategoryCard/Index';
import { WixelCategoryBanner } from './WixelCategoryBanner';
import { mapPageTypeToSourceName } from '../../utils/bi';
import {
  isWixelRelated,
  filterExcludedCategories,
  isTwinsRelated,
} from '../../utils/excluded-categories';
import { useStore } from '../../zustand/stores';

import css from './index.module.scss';

export type CategoryPageWrapperProps = {
  currentCategory: Category;
  categoriesArticles: { [key: string]: Article[] };
  categories: Category[];
};

export type LeftNavClickEventInfo = Pick<
  pageClickParams,
  | 'clicked_item_type'
  | 'clicked_url'
  | 'clicked_text'
  | 'clicked_item_order'
  | 'clicked_item_id'
  | 'category_order'
>;

export const WIX_STUDIO_CATEGORY_ID = '8557ec0a-42e4-48b4-a964-7df1f14df2e0';

export const CategoryPageWrapper: FunctionComponent<
  CategoryPageWrapperProps
> = ({ currentCategory, categoriesArticles, categories }) => {
  const { locale } = useRouter();
  const { sendBIEvent } = useBI();
  const itemId = useStore((state) => state.pageSlice.itemId);
  const setPageMeta = useStore((state) => state.pageSlice.setPageMeta);
  const { setCategoryData } = useStore((state) => state.categorySlice);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<Category>(currentCategory);
  const [categoryArticlesMap, setCategoryArticlesMap] = useState<{
    [key: string]: Article[];
  }>(categoriesArticles);
  const httpClient = useHttpClient();
  const isWixStudioCategory = selectedCategory.id === WIX_STUDIO_CATEGORY_ID;
  const isDevEnv = process.env.NODE_ENV === 'development';

  const navigationMenuClickBI = useCallback(
    async (itemClicked: LeftNavClickEventInfo) => {
      await sendBIEvent(
        pageClick({
          source_name: BI.SOURCE_NAMES.CATEGORY,
          kb_lang: locale as string,
          item_id: selectedCategory.id,
          ...itemClicked,
        })
      );
    },
    [locale, selectedCategory.id]
  );

  const onSelectKnownIssuesCategory = useCallback(
    async (knownIssuesCategory: Category, event?: LeftNavClickEventInfo) => {
      await pushUrl(`${locale}${knownIssuesCategory.uri}`, false, locale);
      if (event) {
        await navigationMenuClickBI(event);
      }
    },
    [locale, navigationMenuClickBI]
  );

  const fetchCategoryTranslations = useCallback(async (categoryId: string) => {
    try {
      const { data } = await httpClient.get<Translation[]>(
        '/api/category/translations',
        {
          params: {
            categoryId,
          },
        }
      );
      return data;
    } catch (error) {
      return [];
    }
  }, []);

  const onCategoryChange = useCallback(
    async (newCategory: Category, event?: LeftNavClickEventInfo) => {
      if (newCategory.id === KNOWN_ISSUE_CATEGORY_ID) {
        await onSelectKnownIssuesCategory(newCategory, event);
        return;
      }
      animateScroll.scrollToTop();
      hideCategoryTree();
      const translations = await fetchCategoryTranslations(newCategory.id);
      setPageMeta({ itemId: newCategory.id });
      setCategoryData({ category: newCategory, translations });
      setSelectedCategory(newCategory);

      if (!newCategory.children || newCategory.children.length === 0) {
        await fetchArticles([newCategory]);
      } else if (newCategory.children) {
        await fetchArticles(newCategory.children);
      }
      if (event) {
        await navigationMenuClickBI(event);
      }
    },
    [setPageMeta, setCategoryData, onSelectKnownIssuesCategory]
  );

  useEffect(() => {
    const onPopState = async (ev: PopStateEvent) => {
      const uri = ev.state.as.replace(`${locale}/`, '');
      if (uri !== selectedCategory.uri) {
        let decodeURI;
        try {
          decodeURI = decodeURIComponent(uri);
        } catch (error) {
          decodeURI = uri;
        }
        const category = findCategoryInTree(
          categories,
          `${decodeURI}`
        ) as Category;
        if (category) {
          await onCategoryChange(category);
        }
      }
    };
    addEventListener('popstate', onPopState);
    return () => removeEventListener('popstate', onPopState);
  }, [categories, selectedCategory.uri]);
  const onArticleClick = async (
    article: Article,
    event: LeftNavClickEventInfo
  ) => {
    hideCategoryTree();
    await navigationMenuClickBI(event);
  };
  const onNodeToggle = async (
    category: Category,
    event: LeftNavClickEventInfo
  ) => {
    await navigationMenuClickBI(event);
  };

  useEffect(
    () => () => {
      document.body.classList.remove('modal-open');
    },
    []
  );
  const getCategoryArticles = useCallback(
    async (categoryIds: string[]) => {
      const { data } = await httpClient.get<{ [key: string]: Article[] }>(
        '/api/category/articles',
        {
          params: {
            locale,
            summarize: true,
            categoryIds,
          },
        }
      );
      return data;
    },
    [httpClient, locale]
  );

  const subcategories = isDevEnv ? selectedCategory?.children : selectedCategory?.children?.filter(
    (child) => child.hasPublishedArticleDescendant
  );

  const fetchArticles = async (fetchCategories: Category[]) => {
    const childrenWithNoChildren = fetchCategories?.filter(
      (child) => !child.children || child.children.length === 0
    );
    const categoryIds = childrenWithNoChildren.map((child) => child.id);

    if (categoryIds.length === 0) {
      return {};
    }
    if (
      categoryIds.length > 1 &&
      categoryIds.every((categoryId) => categoryArticlesMap[categoryId])
    ) {
      return categoryIds.reduce(
        (acc, categoryId) => ({
          ...acc,
          [categoryId]: categoryArticlesMap[categoryId],
        }),
        {}
      ) as { [key: string]: Article[] };
    }
    const articles = await getCategoryArticles(categoryIds);
    setCategoryArticlesMap((prev) => ({ ...prev, ...articles }));

    return articles;
  };
  const topicsWithArticles = (
    categoriesToTransform: Category[]
  ): NavigationTopics[] =>
    filterExcludedCategories(
      categoriesToTransform.filter((cat) => isDevEnv ? true : cat.hasPublishedArticleDescendant)
    ).map((categoryToTransform) => ({
      ...categoryToTransform,
      articles: async () =>
        categoryArticlesMap[categoryToTransform.id] ||
        (await fetchArticles([categoryToTransform]))[categoryToTransform.id],
      children:
        categoryToTransform.children &&
        topicsWithArticles(categoryToTransform.children),
    }));

  const toggleCategoryTree = () => {
    const classList = document.body.classList;
    if (isCategoryMenuOpen) {
      classList.remove('modal-open');
    } else {
      classList.add('modal-open');
    }
    setIsCategoryMenuOpen(!isCategoryMenuOpen);
  };
  const hideCategoryTree = () => {
    setIsCategoryMenuOpen(false);
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
  };
  const onLinkClick = useCallback(
    async (category: Category, clickedItemOrder?: number): Promise<void> => {
      await onCategoryChange(category, {
        clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
        clicked_item_id: category.id,
        clicked_item_order: clickedItemOrder?.toString(),
        clicked_text: category.name,
        clicked_url: category.url,
      });
    },
    [onCategoryChange]
  );
  const onHomePageClick = useCallback(
    async (text: string) => {
      await sendBIEvent(
        pageClick({
          kb_lang: locale as string,
          source_name: mapPageTypeToSourceName(PageType.Category),
          item_id: selectedCategory.id,
          clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
          clicked_url: `${location.origin}/${locale}`,
          clicked_text: text,
        })
      );
    },
    [selectedCategory.id]
  );

  const breadcrumbItems = useMemo(
    () =>
      getCategoriesBreadcrumb(
        categories,
        selectedCategory.id,
        onLinkClick,
        true
      ),
    [selectedCategory, categories, onLinkClick]
  );

  const isWixelCategory = isWixelRelated(
    PageType.Category,
    categories,
    itemId
  );
  const isTwinsCategory = isTwinsRelated(
    PageType.Category,
    categories,
    itemId
  );

  return (
    <div className={css.categoryPage}>
      <Breadcrumbs
        breadcrumbItems={breadcrumbItems}
        onCategoryMenuClick={toggleCategoryTree}
        onHomePageClick={onHomePageClick}
        hideHomePage={isWixelCategory || isTwinsCategory}
        navCategoryTree={
          !isWixelCategory && !isTwinsCategory ? (
            <NavCategoryTree
              onArticleClick={onArticleClick}
              isCategoryMenuOpen={isCategoryMenuOpen}
              onCategoryChange={onCategoryChange}
              topics={topicsWithArticles(categories)}
              selectedCategoryId={selectedCategory.id}
              onNodeToggle={onNodeToggle}
              onCategoryMenuClose={hideCategoryTree}
              locale={locale}
            />
          ) : undefined
        }
        shallow
      />
      <Box position="relative" direction="horizontal">
        <Box className={css.navCategoryTreeWrapper}>
          {!isWixelCategory && !isTwinsCategory && (
            <NavCategoryTree
              onArticleClick={onArticleClick}
              isCategoryMenuOpen={isCategoryMenuOpen}
              onCategoryChange={onCategoryChange}
              topics={topicsWithArticles(categories)}
              selectedCategoryId={selectedCategory.id}
              onNodeToggle={onNodeToggle}
              onCategoryMenuClose={hideCategoryTree}
              locale={locale}
            />
          )}
        </Box>
        {subcategories && subcategories.length > 0 ? (
          <Box className={css.categoryDetailsWrapper} align="center">
            <Box direction="vertical">
              {selectedCategory.id === WIXEL_CATEGORY_ID ? (
                <WixelCategoryBanner category={selectedCategory} />
              ) : (
                <CategoryPageHeader selectedCategory={selectedCategory} />
              )}
              <div className={css.subcategories}>
                {subcategories.map((subcategory, idx) => (
                  <Box
                    key={subcategory.id}
                    className={css.subcategory}
                    direction="vertical"
                  >
                    <SubcategoryCard
                      categoryOrder={idx + 1}
                      onClickSubcategory={onCategoryChange}
                      onClickArticle={onArticleClick}
                      category={subcategory}
                      articles={categoryArticlesMap[subcategory.id]}
                    />
                  </Box>
                ))}
              </div>
              {isWixStudioCategory ? (
                <div className={css.StudioCategoryAddonWrapper}>
                  <StudioCategoryAddon
                    categoryId={selectedCategory.id}
                    lastItemOrder={subcategories.length}
                  />
                </div>
              ) : null}
            </Box>
          </Box>
        ) : (
          <ArticleList
            category={selectedCategory}
            categoryArticles={categoryArticlesMap[selectedCategory.id]}
          />
        )}
      </Box>
    </div>
  );
};
